import { useEffect, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { notificationsAPI } from '../../services/api';
import BrandLogo from './BrandLogo';
import UserProfileMenu from './UserProfileMenu';
import { Nav } from '../icons/NavIcon';

const adminNavGroups = [
  {
    heading: 'Global',
    items: [
      { path: '/admin/dashboard', icon: Nav.pieChart, label: 'Dashboard' },
      { path: '/admin/clients', icon: Nav.users, label: 'Client accounts' },
      { path: '/admin/projects', icon: Nav.layers, label: 'All sites' },
      { path: '/admin/support', icon: Nav.lifeBuoy, label: 'Ticket manager' },
    ],
  },
  {
    heading: 'Operations',
    items: [
      { path: '/admin/inbox', icon: Nav.inbox, label: 'Administrator inbox', badgeKey: 'adminInbox' },
      { path: '/admin/data-platform', icon: Nav.database, label: 'Data platform' },
      { path: '/admin/log-analytics', icon: Nav.activity, label: 'Log analytics' },
      { path: '/admin/backup-restore', icon: Nav.database, label: 'Backup & restore' },
      { path: '/admin/experiments', icon: Nav.cpu, label: 'A/B tests (audit)' },
      { path: '/admin/layers', icon: Nav.layers, label: 'OEI domains & layers' },
      { path: '/admin/holdout-report', icon: Nav.shield, label: 'Holdout cohort report' },
      { path: '/admin/metrics', icon: Nav.barChart2, label: 'Metric catalog' },
      { path: '/admin/statistical-defaults', icon: Nav.activity, label: 'Statistical defaults' },
      { path: '/admin/commerce', icon: Nav.creditCard, label: 'Commerce / orders' },
    ],
  },
  {
    heading: 'Configuration',
    items: [
      { path: '/admin/plans', icon: Nav.package, label: 'Plans & metering' },
      { path: '/admin/service-packages', icon: Nav.tag, label: 'Add-on pricing' },
      { path: '/admin/ai-settings', icon: Nav.zap, label: 'AI / LLM routing' },
      { path: '/admin/telemetry', icon: Nav.radio, label: 'Live data feed' },
    ],
  },
  {
    heading: 'Account',
    items: [{ path: '/admin/settings', icon: Nav.settings, label: 'Profile & settings' }],
  },
];

export default function AdminSidebar({ isCollapsed = false, onToggleCollapse }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [adminInboxUnread, setAdminInboxUnread] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await notificationsAPI.list({ limit: 1, surface: 'admin' });
        if (!cancelled) setAdminInboxUnread(data.data?.unreadCount ?? 0);
      } catch {
        if (!cancelled) setAdminInboxUnread(0);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <aside
      className={`fixed left-0 top-0 h-full bg-txnweb-canvas border-r border-black/[0.06] flex flex-col z-50 ${isCollapsed ? 'w-20' : 'w-64'}`}
      aria-label="Admin navigation"
    >
      <div className={`border-b border-black/[0.06] ${isCollapsed ? 'px-3 pt-4 pb-4' : 'px-5 pt-6 pb-5'}`}>
        <div className={`flex items-start ${isCollapsed ? 'justify-center' : 'justify-between gap-2'}`}>
          {!isCollapsed && <BrandLogo variant="compact" />}
          <button
            type="button"
            onClick={onToggleCollapse}
            aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-black/[0.08] text-text-secondary hover:text-text-primary hover:bg-black/[0.03] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0071E3]/35"
          >
            {isCollapsed ? <Nav.chevronsRight size={16} aria-hidden /> : <Nav.chevronsLeft size={16} aria-hidden />}
          </button>
        </div>
        {!isCollapsed && (
          <p className="text-[13px] text-txnweb-muted mt-3 leading-snug font-medium">
            Global view of clients, connected sites, tickets, and platform configuration.
          </p>
        )}
        {isCollapsed && (
          <div className="sr-only">
            <BrandLogo variant="compact" />
          </div>
        )}
      </div>

      <nav className={`txnweb-sidebar-nav flex-1 py-3 overflow-y-auto ${isCollapsed ? 'px-2' : 'px-3'}`}>
        {adminNavGroups.map((group) => (
          <div key={group.heading} className="mb-4 last:mb-0">
            {!isCollapsed && <p className="sidebar-nav-heading">{group.heading}</p>}
            <div className="space-y-0.5">
              {group.items.map(({ path, icon: Icon, label, badgeKey }) => {
                const unread =
                  badgeKey === 'adminInbox' ? adminInboxUnread : 0;
                return (
                  <NavLink
                    key={path}
                    to={path}
                    className={({ isActive }) => {
                      const base = isActive ? 'sidebar-link-active' : 'sidebar-link';
                      return `${base} ${isCollapsed ? 'justify-center px-0' : ''} ${!isCollapsed && unread > 0 ? 'pr-2' : ''}`;
                    }}
                    end={path === '/admin/dashboard'}
                    title={isCollapsed ? (unread > 0 ? `${label} (${unread} unread)` : label) : undefined}
                    aria-label={unread > 0 ? `${label}, ${unread} unread` : label}
                  >
                    <span className="relative shrink-0">
                      <Icon size={20} className="opacity-90" aria-hidden />
                      {isCollapsed && unread > 0 && (
                        <span
                          className="absolute -top-1 -right-1 min-w-[8px] h-2 rounded-full bg-rose-600 ring-2 ring-txnweb-canvas"
                          aria-hidden
                        />
                      )}
                    </span>
                    {!isCollapsed && (
                      <>
                        <span className="flex-1 min-w-0 truncate">{label}</span>
                        {unread > 0 && (
                          <span className="badge badge-warning text-[11px] px-1.5 py-0 min-w-[1.25rem] justify-center">
                            {unread > 99 ? '99+' : unread}
                          </span>
                        )}
                      </>
                    )}
                  </NavLink>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      <div className={`border-t border-black/[0.06] ${isCollapsed ? 'p-3' : 'p-4'}`}>
        <UserProfileMenu
          settingsPath="/admin/settings"
          subtitle={
            <>
              Platform administrator
              <span className="block text-[11px] text-text-tertiary font-normal">Not on a customer plan</span>
            </>
          }
          isCollapsed={isCollapsed}
          onLogout={handleLogout}
        />
      </div>
    </aside>
  );
}
